import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

interface FingerprintEnrollmentDeviceOption {
  deviceId: string;
  name: string;
  serial?: string;
}

export interface FingerprintEnrollmentDialogData {
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  devices: FingerprintEnrollmentDeviceOption[];
}

export interface FingerprintEnrollmentDialogResult {
  deviceId: string;
  employeeNumber: string;
  fingerId: number;
  retry: number;
  overwrite: number;
}

interface FingerOption {
  id: number;
  hand: 'left' | 'right';
  labelKey: string;
  position: 'thumb' | 'index' | 'middle' | 'ring' | 'little';
}

@Component({
  selector: 'app-employee-fingerprint-enrollment-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    TranslateModule,
  ],
  templateUrl: './employee-fingerprint-enrollment-dialog.component.html',
  styleUrl: './employee-fingerprint-enrollment-dialog.component.scss',
})
export class EmployeeFingerprintEnrollmentDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<EmployeeFingerprintEnrollmentDialogComponent>);
  private readonly translate = inject(TranslateService);
  private readonly apiService = inject(AuthorizedApiService);
  readonly data = inject<FingerprintEnrollmentDialogData>(MAT_DIALOG_DATA);

  readonly fingers: FingerOption[] = [
    { id: 0, hand: 'left', labelKey: 'little', position: 'little' },
    { id: 1, hand: 'left', labelKey: 'ring', position: 'ring' },
    { id: 2, hand: 'left', labelKey: 'middle', position: 'middle' },
    { id: 3, hand: 'left', labelKey: 'index', position: 'index' },
    { id: 4, hand: 'left', labelKey: 'thumb', position: 'thumb' },
    { id: 5, hand: 'right', labelKey: 'thumb', position: 'thumb' },
    { id: 6, hand: 'right', labelKey: 'index', position: 'index' },
    { id: 7, hand: 'right', labelKey: 'middle', position: 'middle' },
    { id: 8, hand: 'right', labelKey: 'ring', position: 'ring' },
    { id: 9, hand: 'right', labelKey: 'little', position: 'little' },
  ];

  selectedDeviceId = this.data.devices[0]?.deviceId || '';
  selectedFingerId: number | null = null;
  expandedHand: 'left' | 'right' = 'left';
  enrolledFingerIds = new Set<number>();
  isLoadingBiometrics = true;

  constructor() {
    void this.loadExistingFingerprintTemplates();
  }

  get leftFingers(): FingerOption[] {
    return this.fingers.filter((finger) => finger.hand === 'left');
  }

  get rightFingers(): FingerOption[] {
    return this.fingers.filter((finger) => finger.hand === 'right');
  }

  get canSubmit(): boolean {
    return !!this.selectedDeviceId && this.selectedFingerId !== null && !this.isFingerDisabled(this.selectedFingerId);
  }

  get selectedFinger(): FingerOption | undefined {
    return this.fingers.find((finger) => finger.id === this.selectedFingerId);
  }

  selectFinger(fingerId: number): void {
    if (this.isFingerDisabled(fingerId)) {
      return;
    }
    this.selectedFingerId = fingerId;
  }

  toggleHand(hand: 'left' | 'right'): void {
    this.expandedHand = this.expandedHand === hand ? hand : hand;
  }

  close(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (!this.canSubmit || this.selectedFingerId === null) {
      return;
    }

    this.dialogRef.close({
      deviceId: this.selectedDeviceId,
      employeeNumber: this.data.employeeNumber,
      fingerId: this.selectedFingerId,
      retry: 3,
      overwrite: 1,
    } satisfies FingerprintEnrollmentDialogResult);
  }

  getFingerLabel(finger: FingerOption): string {
    return this.translate.instant(`employeesCatalog.fingerprintEnrollment.fingers.${finger.hand}.${finger.labelKey}`);
  }

  getFingerPositionClass(finger: FingerOption): string {
    return `fingerprint-dialog__finger--${finger.position} fingerprint-dialog__finger-id--${finger.id}`;
  }

  isFingerDisabled(fingerId: number): boolean {
    return this.enrolledFingerIds.has(fingerId);
  }

  private async loadExistingFingerprintTemplates(): Promise<void> {
    if (!this.data.employeeId) {
      this.isLoadingBiometrics = false;
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<{ data?: Array<{ modality?: string; templateSlot?: number | null; isActive?: boolean }> }>(
          `employees/${this.data.employeeId}/biometric-templates`
        )
      );

      const enrolledFingerIds = (response.data || [])
        .filter((template) => template.modality === 'fingerprint' && template.isActive !== false && typeof template.templateSlot === 'number')
        .map((template) => template.templateSlot as number)
        .filter((templateSlot) => templateSlot >= 0 && templateSlot <= 9);

      this.enrolledFingerIds = new Set(enrolledFingerIds);

      if (this.selectedFingerId !== null && this.isFingerDisabled(this.selectedFingerId)) {
        this.selectedFingerId = null;
      }
    } catch (error) {
      console.error('Error loading employee biometric templates', error);
      this.enrolledFingerIds = new Set<number>();
    } finally {
      this.isLoadingBiometrics = false;
    }
  }
}
